import {
  Attachment as SigningStateAttachment,
  CheckboxField as SigningStateCheckboxField,
  FieldNode as SigningStateFieldNode,
  FieldNodeType,
  Host as SigningStateHost,
  Participant as SigningStateParticipant,
  Requester as SigningStateRequester,
  Signer as SigningStateSigner,
  SigningState,
  Verification,
} from '@modusign/react-signing/types';
import pick from 'lodash/pick';
import { DeepReadonly } from 'ts-essentials';

import { DEFAULT_SIGNING_DURATION } from '@app/constants/signingDuration';
import formatAddressToString from '@app/features/apply-react-signing/libs/formatAddressToString';
import { Attachment as TransportLayerAttachment } from '@app/store/transport-layer/types/attachment';
import {
  AddressField as TransportLayerAddressField,
  CheckboxField as TransportLayerCheckboxField,
  CompanyNameField as TransportLayerCompanyNameField,
  DateField as TransportLayerDateField,
  DropdownField as TransportLayerDropdownField,
  Field as TransportLayerField,
  ImageFieldToSend as TransportLayerImageField,
  NameField as TransportLayerNameField,
  SignatureField as TransportLayerSignatureField,
  SigningDateField as TransportLayerSigningDateField,
  TextField as TransportLayerTextField,
} from '@app/store/transport-layer/types/field';
import {
  AutofillFieldGroup as TransportLayerAutofillFieldGroup,
  CheckboxFieldGroup as TransportLayerCheckboxFieldGroup,
  FieldGroup as TransportLayerFieldGroup,
} from '@app/store/transport-layer/types/fieldGroup';
import {
  ParticipantType,
  ParticipantWithSigningProperties as TransportLayerParticipant,
} from '@app/store/transport-layer/types/participant';
import { WithId } from '@app/store/transport-layer/types/utility';
import {
  Verification as TransportLayerVerification,
  VerificationMethod,
} from '@app/store/transport-layer/types/verification';

export type DocumentModel = {
  attachments: TransportLayerAttachment['ToSend'][];
  fields: TransportLayerField['ToSend'][];
  fieldGroups: TransportLayerFieldGroup[];
  participants: TransportLayerParticipant[];
  verifications: TransportLayerVerification[];
};

type MappingFrom = 'SIGNING' | 'SETUP';

export function toDocumentModelForSetup(src: DeepReadonly<SigningState>): DocumentModel {
  const { fields, fieldGroups } = resolveField(src.fields as SigningStateFieldNode[], 'SETUP');

  return {
    participants: src.participants.map<TransportLayerParticipant>((p) =>
      participantMapper[p.type](p)
    ),
    attachments: src.attachments.map((a) => attachmentMapper(a, 'SETUP')),
    fields,
    fieldGroups,
    verifications: src.verifications.map((a) => verificationMapper(a, 'SETUP')),
  };
}

export function toDocumentModelForSigning(src: DeepReadonly<SigningState>): DocumentModel {
  const { fields, fieldGroups } = resolveField(src.fields as SigningStateFieldNode[], 'SIGNING');

  return {
    participants: src.participants.map<TransportLayerParticipant>((p) =>
      participantMapper[p.type](p)
    ),
    attachments: src.attachments.map((a) => attachmentMapper(a, 'SIGNING')),
    fields,
    fieldGroups,
    verifications: src.verifications.map((a) => verificationMapper(a, 'SIGNING')),
  };
}

export function getFieldCounts(fields: DeepReadonly<SigningStateFieldNode[]>) {
  return fields.reduce<{
    REQUESTER: { [key: string]: number };
    SIGNER: { [key: string]: number };
    HOST: { [key: string]: number };
  }>(
    (acc, field) => {
      // 자동 완성 그룹의 경우 모든 자식 필드의 타입을 카운트합니다.
      if (field.type === 'AUTOFILL_GROUP') {
        field.children.forEach((child) => {
          acc[field.participantReference.type][child.type] =
            (acc[field.participantReference.type][child.type] || 0) + 1;
        });
      } else {
        acc[field.participantReference.type][field.type] =
          (acc[field.participantReference.type][field.type] || 0) + 1;
      }
      return acc;
    },
    { REQUESTER: {}, SIGNER: {}, HOST: {} }
  );
}

type ParticipantMapperType = {
  [K in ParticipantType]: (
    src: SigningStateParticipant
  ) => Extract<TransportLayerParticipant, { type: K }>;
};

const participantMapper: ParticipantMapperType = {
  SIGNER: (participant) => {
    const signer = participant as SigningStateSigner;
    return {
      dataLabel: signer.dataLabel,
      order: signer.order,
      signingMethod: signer.signingMethod,
      type: 'SIGNER',
      contact: signer.contact,
      name: signer.name,
      role: signer.role,
      host: signer.host,
      signingDuration: signer.signingDuration ?? DEFAULT_SIGNING_DURATION,
      signingPeriod: null,
      locale: signer.locale ?? 'ko',
      message: signer.message?.trim() || null,
    };
  },
  REQUESTER: (participant) => {
    const requester = participant as SigningStateRequester;
    return {
      dataLabel: requester.dataLabel,
      type: 'REQUESTER',
      contact: requester.contact,
      name: requester.name,
    };
  },
  HOST: (participant) => {
    const host = participant as SigningStateHost;
    return {
      contact: host.contact,
      dataLabel: host.dataLabel,
      name: host.name,
      type: 'HOST',
      id: host.id,
      locale: host.locale,
    };
  },
};

const toParticipantRef = (src: SigningStateParticipant, from: MappingFrom) =>
  pick(src, from === 'SIGNING' ? ['id', 'dataLabel'] : ['dataLabel']);

function attachmentMapper(
  src: SigningStateAttachment,
  from: MappingFrom
): TransportLayerAttachment['ToSend'] {
  return {
    config: src.config,
    input: src.input
      ? {
          file: src.input,
        }
      : null,
    message: src.message || null,
    participant: toParticipantRef(src.participantReference, from),
    title: src.title,
    dataLabel: src.dataLabel,
    documentId: src.documentId,
  };
}

function verificationMapper(
  src: DeepReadonly<Verification>,
  from: MappingFrom
): TransportLayerVerification {
  return {
    participant: toParticipantRef(src.participantReference, from),
    methods: src.methods
      .map<VerificationMethod | null>((method) => {
        switch (method.type) {
          case 'PASSWORD':
            return {
              type: 'PASSWORD',
              hint: method.hint || null,
              passValue: method.passValue,
            };
          case 'MOBILE_IDENTIFICATION':
            return {
              type: 'MOBILE_IDENTIFICATION',
              passValue: method.passValue,
              hint: method.hint || null,
              displayName: method.displayName || null,
              displayPhoneNumber: method.displayPhoneNumber || null,
            };
          case 'D_CERT':
            return {
              type: 'D_CERT',
              passValue: method.passValue,
              hint: method.hint || null,
              displayName: method.displayName || null,
              displayBizNumber: method.displayBizNumber || null,
            };
          default:
            return null;
        }
      })
      .filter((method): method is VerificationMethod => method !== null),
  };
}

type FieldMapperOutput = {
  fields: Array<WithId<DocumentModel['fields'][0]>>;
  fieldGroups: DocumentModel['fieldGroups'];
};

export function resolveField(src: SigningStateFieldNode[], from: MappingFrom): FieldMapperOutput {
  const output: FieldMapperOutput = {
    fieldGroups: [],
    fields: [],
  };

  src.forEach((fieldNode) => {
    fieldMapper[fieldNode.type](fieldNode, output, from);
  });

  return output;
}

type FieldMapperType = {
  [K in FieldNodeType]: (
    current: SigningStateFieldNode,
    output: FieldMapperOutput,
    from: MappingFrom
  ) => void;
};

const fieldMapper: FieldMapperType = {
  TEXT: textFieldMapper,
  SIGNATURE: signatureFieldMapper,
  IMAGE: imageFieldMapper,
  CHECKBOX: checkboxFieldMapper,
  CHECKBOX_GROUP: checkboxFieldGroupMapper,
  DROPDOWN: dropdownFieldMapper,
  AUTOFILL_GROUP: autoFillFieldGroupMapper,
  NAME: nameFieldMapper,
  COMPANY_NAME: companyNameFieldMapper,
  ADDRESS: addressFieldMapper,
  DATE: dateFieldMapper,
  SIGNING_DATE: signingDateFieldMapper,
};

function textFieldMapper(
  current: SigningStateFieldNode,
  output: FieldMapperOutput,
  from: MappingFrom
) {
  if (current.type !== 'TEXT') {
    return;
  }

  const { config, input } = current;
  const mapValueDescription = (value?: string | null) =>
    value ? value.replaceAll(/\s+/g, ' ').trim() || null : null;

  const transportLayerConfig: TransportLayerTextField['config'] = {
    changeableAttributes: [],
    defaultInputValue: config.defaultInputValue,
    disabled: false,
    fontStyle: config.fontStyle,
    placeholder: config.placeholder,
    format: config.format,
    required: config.required,
    textAlign: config.textAlign,
    valueType: 'STRING',
    valueDescription: mapValueDescription(
      config.format === null && config.valueDescription ? config.valueDescription : null
    ),
  };

  const transportLayerOverrideConfig: TransportLayerTextField['config'] = {
    changeableAttributes: [],
    defaultInputValue: input?.overridingConfig.defaultInputValue ?? config.defaultInputValue,
    disabled: false,
    fontStyle: input?.overridingConfig.fontStyle ?? config.fontStyle,
    placeholder: input?.overridingConfig.placeholder ?? config.placeholder,
    format: input?.overridingConfig.format ?? config.format,
    required: input?.overridingConfig.required ?? config.required,
    textAlign: input?.overridingConfig.textAlign ?? config.textAlign,
    valueDescription: mapValueDescription(
      input?.overridingConfig.valueDescription || config.valueDescription
    ),
    valueType: 'STRING',
  };

  const textField: WithId<TransportLayerTextField> = {
    id: current.id,
    type: 'TEXT',
    config: transportLayerConfig,
    dataLabel: current.dataLabel,
    groupDataLabel: null,
    guide: {
      position: current.position,
      size: current.size,
    },
    input:
      input && input.value
        ? {
            overrideConfig: transportLayerOverrideConfig,
            position: current.position,
            size: current.size,
            value: input.value,
          }
        : null,
    participant: toParticipantRef(current.participantReference, from),
  };
  output.fields.push(textField);
}

function nameFieldMapper(
  current: SigningStateFieldNode,
  output: FieldMapperOutput,
  from: MappingFrom
) {
  if (current.type !== 'NAME') {
    return;
  }

  const { config, input } = current;

  const transportLayerConfig: TransportLayerNameField['config'] = {
    changeableAttributes: [],
    defaultInputValue: config.defaultInputValue,
    disabled: false,
    fontStyle: config.fontStyle,
    placeholder: config.placeholder,
    required: config.required,
    textAlign: config.textAlign,
    valueType: 'STRING',
  };

  const transportLayerOverrideConfig: TransportLayerNameField['config'] = {
    changeableAttributes: [],
    defaultInputValue: input?.overridingConfig.defaultInputValue || config.defaultInputValue,
    disabled: false,
    fontStyle: input?.overridingConfig.fontStyle || config.fontStyle,
    placeholder: input?.overridingConfig.placeholder || config.placeholder,
    required: input?.overridingConfig.required || config.required,
    textAlign: input?.overridingConfig.textAlign || config.textAlign,
    valueType: 'STRING',
  };

  const field: WithId<TransportLayerNameField> = {
    id: current.id,
    type: 'NAME',
    config: transportLayerConfig,
    dataLabel: current.dataLabel,
    groupDataLabel: current.parent?.dataLabel || null,
    guide: {
      position: current.position,
      size: current.size,
    },
    input:
      input && input.value
        ? {
            overrideConfig: transportLayerOverrideConfig,
            position: current.position,
            size: current.size,
            value: input.value,
          }
        : null,
    participant: toParticipantRef(current.participantReference, from),
  };
  output.fields.push(field);
}

function companyNameFieldMapper(
  current: SigningStateFieldNode,
  output: FieldMapperOutput,
  from: MappingFrom
) {
  if (current.type !== 'COMPANY_NAME') {
    return;
  }

  const { config, input } = current;

  const transportLayerConfig: TransportLayerCompanyNameField['config'] = {
    changeableAttributes: [],
    defaultInputValue: config.defaultInputValue,
    disabled: false,
    fontStyle: config.fontStyle,
    placeholder: config.placeholder,
    required: config.required,
    textAlign: config.textAlign,
    valueType: 'STRING',
  };

  const transportLayerOverrideConfig: TransportLayerCompanyNameField['config'] = {
    changeableAttributes: [],
    defaultInputValue: input?.overridingConfig.defaultInputValue || config.defaultInputValue,
    disabled: false,
    fontStyle: input?.overridingConfig.fontStyle || config.fontStyle,
    placeholder: input?.overridingConfig.placeholder || config.placeholder,
    required: input?.overridingConfig.required || config.required,
    textAlign: input?.overridingConfig.textAlign || config.textAlign,
    valueType: 'STRING',
  };

  const field: WithId<TransportLayerCompanyNameField> = {
    id: current.id,
    type: 'COMPANY_NAME',
    config: transportLayerConfig,
    dataLabel: current.dataLabel,
    groupDataLabel: current.parent?.dataLabel || null,
    guide: {
      position: current.position,
      size: current.size,
    },
    input:
      input && input.value
        ? {
            overrideConfig: transportLayerOverrideConfig,
            position: current.position,
            size: current.size,
            value: input.value,
          }
        : null,
    participant: toParticipantRef(current.participantReference, from),
  };
  output.fields.push(field);
}

function addressFieldMapper(
  current: SigningStateFieldNode,
  output: FieldMapperOutput,
  from: MappingFrom
) {
  if (current.type !== 'ADDRESS') {
    return;
  }

  const { config, input } = current;

  const transportLayerConfig: TransportLayerAddressField['config'] = {
    changeableAttributes: [], // TODO: @DevSDK 수정불가 어떻게 푸냐에 따라 값 줘야할수도
    defaultInputValue: config.defaultInputValue,
    disabled: false,
    fontStyle: config.fontStyle,
    placeholder: config.placeholder,
    required: config.required,
    textAlign: config.textAlign,
    valueType: 'ADDRESS',
  };

  const transportLayerOverrideConfig: TransportLayerAddressField['config'] = {
    changeableAttributes: [],
    defaultInputValue: input?.overridingConfig.defaultInputValue || config.defaultInputValue,
    disabled: false,
    fontStyle: input?.overridingConfig.fontStyle || config.fontStyle,
    placeholder: input?.overridingConfig.placeholder || config.placeholder,
    required: input?.overridingConfig.required || config.required,
    textAlign: input?.overridingConfig.textAlign || config.textAlign,
    valueType: 'ADDRESS',
  };

  const field: WithId<TransportLayerAddressField> = {
    id: current.id,
    type: 'ADDRESS',
    config: transportLayerConfig,
    dataLabel: current.dataLabel,
    groupDataLabel: current.parent?.dataLabel || null,
    guide: {
      position: current.position,
      size: current.size,
    },
    input:
      input && formatAddressToString(input.value)
        ? {
            overrideConfig: transportLayerOverrideConfig,
            position: current.position,
            size: current.size,
            value: input.value,
          }
        : null,
    participant: toParticipantRef(current.participantReference, from),
  };
  output.fields.push(field);
}

function dateFieldMapper(
  current: SigningStateFieldNode,
  output: FieldMapperOutput,
  from: MappingFrom
) {
  if (current.type !== 'DATE') {
    return;
  }

  const { config, input } = current;

  const transportLayerConfig: TransportLayerDateField['config'] = {
    changeableAttributes: [],
    defaultInputValue: config.defaultInputValue,
    disabled: false,
    fontStyle: config.fontStyle,
    placeholder: config.placeholder,
    required: config.required,
    textAlign: config.textAlign,
    displayFormat: config.displayFormat,
    valueType: 'STRING',
  };

  const transportLayerOverrideConfig: TransportLayerDateField['config'] = {
    changeableAttributes: [],
    defaultInputValue: input?.overridingConfig.defaultInputValue || config.defaultInputValue,
    disabled: false,
    fontStyle: input?.overridingConfig.fontStyle || config.fontStyle,
    placeholder: input?.overridingConfig.placeholder || config.placeholder,
    required: input?.overridingConfig.required || config.required,
    textAlign: input?.overridingConfig.textAlign || config.textAlign,
    displayFormat: config.displayFormat,
    valueType: 'STRING',
  };

  const field: WithId<TransportLayerDateField> = {
    id: current.id,
    type: 'DATE',
    config: transportLayerConfig,
    dataLabel: current.dataLabel,
    groupDataLabel: null,
    guide: {
      position: current.position,
      size: current.size,
    },
    input:
      input && input.value
        ? {
            overrideConfig: transportLayerOverrideConfig,
            position: current.position,
            size: current.size,
            value: input.value,
          }
        : null,
    participant: toParticipantRef(current.participantReference, from),
  };
  output.fields.push(field);
}

function signingDateFieldMapper(
  current: SigningStateFieldNode,
  output: FieldMapperOutput,
  from: MappingFrom
) {
  if (current.type !== 'SIGNING_DATE') {
    return;
  }

  const { config, input } = current;

  const transportLayerConfig: TransportLayerSigningDateField['config'] = {
    changeableAttributes: [],
    defaultInputValue: config.defaultInputValue,
    disabled: false,
    fontStyle: config.fontStyle,
    placeholder: config.placeholder,
    required: config.required,
    textAlign: config.textAlign,
    displayFormat: config.displayFormat,
    valueType: 'STRING',
  };

  const transportLayerOverrideConfig: TransportLayerSigningDateField['config'] = {
    changeableAttributes: [],
    defaultInputValue: input?.overridingConfig.defaultInputValue || config.defaultInputValue,
    disabled: false,
    fontStyle: input?.overridingConfig.fontStyle || config.fontStyle,
    placeholder: input?.overridingConfig.placeholder || config.placeholder,
    required: input?.overridingConfig.required || config.required,
    textAlign: input?.overridingConfig.textAlign || config.textAlign,
    displayFormat: config.displayFormat,
    valueType: 'STRING',
  };

  const field: WithId<TransportLayerSigningDateField> = {
    id: current.id,
    type: 'SIGNING_DATE',
    config: transportLayerConfig,
    dataLabel: current.dataLabel,
    groupDataLabel: null,
    guide: {
      position: current.position,
      size: current.size,
    },
    input:
      input && input.value
        ? {
            overrideConfig: transportLayerOverrideConfig,
            position: current.position,
            size: current.size,
            value: input.value,
          }
        : null,
    participant: toParticipantRef(current.participantReference, from),
  };
  output.fields.push(field);
}

function dropdownFieldMapper(
  current: SigningStateFieldNode,
  output: FieldMapperOutput,
  from: MappingFrom
) {
  if (current.type !== 'DROPDOWN') {
    return;
  }

  const { config, input } = current;

  const transportLayerConfig: TransportLayerDropdownField['config'] = {
    changeableAttributes: [],
    defaultInputValue: config.defaultInputValue,
    disabled: false,
    fontStyle: config.fontStyle,
    placeholder: config.placeholder,
    required: config.required,
    textAlign: config.textAlign,
    options: config.options.map(({ value }) => ({ value: value.replace(/[\s]+/g, ' ') })),
    valueType: 'STRING',
  };

  const transportLayerOverrideConfig: TransportLayerDropdownField['config'] = {
    changeableAttributes: [],
    defaultInputValue: input?.overridingConfig.defaultInputValue || config.defaultInputValue,
    disabled: false,
    fontStyle: input?.overridingConfig.fontStyle || config.fontStyle,
    placeholder: input?.overridingConfig.placeholder || config.placeholder,
    required: input?.overridingConfig.required || config.required,
    textAlign: input?.overridingConfig.textAlign || config.textAlign,
    // 개행문자는 select 표준에 의해 제거됩니다.
    // FYI: https://github.com/modusign/modusign-client/pull/9981
    options: (input?.overridingConfig.options || config.options).map(({ value }) => ({
      value: value.replace(/[\s]+/g, ' '),
    })),
    valueType: 'STRING',
  };

  const textField: WithId<TransportLayerDropdownField> = {
    id: current.id,
    type: 'DROPDOWN',
    config: transportLayerConfig,
    dataLabel: current.dataLabel,
    groupDataLabel: null,
    guide: {
      position: current.position,
      size: current.size,
    },
    input:
      input && input.value
        ? {
            overrideConfig: transportLayerOverrideConfig,
            position: current.position,
            size: current.size,
            value: input.value,
          }
        : null,
    participant: toParticipantRef(current.participantReference, from),
  };
  output.fields.push(textField);
}

function signatureFieldMapper(
  current: SigningStateFieldNode,
  output: FieldMapperOutput,
  from: MappingFrom
) {
  if (current.type !== 'SIGNATURE') {
    return;
  }
  const signatureFieldConfig = {
    allow: current.config.allow,
    changeableAttributes: [],
    disabled: false,
    disableSignatureGenerator: current.config.disableSignatureGenerator,
    required: current.config.required,
    valueType: 'SIGNATURE' as const,
  };

  const signatureFieldOverrideConfig = {
    allow: current.input?.overridingConfig.allow || current.config.allow,
    changeableAttributes: [],
    disabled: false,
    disableSignatureGenerator: current.config.disableSignatureGenerator,
    required: current.input?.overridingConfig.required || current.config.required,
    valueType: 'SIGNATURE' as const,
  };

  const signatureField: WithId<TransportLayerSignatureField> = {
    id: current.id,
    config: signatureFieldConfig,
    dataLabel: current.dataLabel,
    groupDataLabel: null,
    guide: {
      position: current.position,
      size: current.size,
    },
    input: current.input?.value
      ? {
          overrideConfig: signatureFieldOverrideConfig,
          position: current.input.position,
          size: current.input.size,
          value: current.input.value,
        }
      : null,
    participant: toParticipantRef(current.participantReference, from),
    type: 'SIGNATURE',
  };
  output.fields.push(signatureField);
}

function imageFieldMapper(
  current: SigningStateFieldNode,
  output: FieldMapperOutput,
  from: MappingFrom
) {
  if (current.type !== 'IMAGE') {
    return;
  }

  const imageFieldConfig: TransportLayerImageField['config'] = {
    changeableAttributes: [],
    disabled: false,
    required: current.config.required,
    valueType: 'IMAGE',
  };

  const imageFieldOverrideConfig: TransportLayerImageField['config'] = {
    changeableAttributes: [],
    disabled: false,
    required: current.input?.overridingConfig.required || current.config.required,
    valueType: 'IMAGE',
  };

  const imageField: WithId<TransportLayerImageField> = {
    id: current.id,
    config: imageFieldConfig,
    dataLabel: current.dataLabel,
    groupDataLabel: null,
    guide: {
      position: current.position,
      size: current.size,
    },
    input:
      current.input && current.input.value.fileId
        ? {
            overrideConfig: imageFieldOverrideConfig,
            position: current.position,
            size: current.size,
            value: {
              fileId: current.input.value.fileId,
              otp: current.input.value.otp,
            },
          }
        : null,
    participant: toParticipantRef(current.participantReference, from),
    type: 'IMAGE',
  };
  output.fields.push(imageField);
}

function checkboxFieldMapper(
  current: SigningStateFieldNode,
  output: FieldMapperOutput,
  from: MappingFrom
) {
  if (current.type !== 'CHECKBOX') {
    return;
  }

  const checkboxFieldConfig: TransportLayerCheckboxField['config'] = {
    changeableAttributes: [],
    disabled: false,
    required: current.config.required,
    valueType: 'BOOLEAN',
  };

  const checkboxFieldOverrideConfig: TransportLayerCheckboxField['config'] = {
    changeableAttributes: [],
    disabled: false,
    required: current.input?.overridingConfig.required || current.config.required,
    valueType: 'BOOLEAN',
  };

  const checkboxField: WithId<TransportLayerCheckboxField> = {
    id: current.id,
    config: checkboxFieldConfig,
    dataLabel: current.dataLabel,
    groupDataLabel: current.parent?.dataLabel || null,
    guide: {
      position: current.position,
      size: current.size,
    },
    input:
      current.input && current.input.value
        ? {
            overrideConfig: checkboxFieldOverrideConfig,
            position: current.position,
            size: current.size,
            value: 'TRUE',
          }
        : null,
    participant: toParticipantRef(current.participantReference, from),
    type: 'CHECKBOX',
  };
  output.fields.push(checkboxField);
}

function checkboxFieldGroupMapper(
  current: SigningStateFieldNode,
  output: FieldMapperOutput,
  from: MappingFrom
) {
  if (current.type !== 'CHECKBOX_GROUP') {
    return;
  }

  if (current.children.length === 1) {
    if (current.children[0].type === 'CHECKBOX') {
      const singleCheckbox: SigningStateCheckboxField = {
        ...current.children[0],
        parent: null,
        dataLabel: current.dataLabel,
        config: {
          required: current.config.required,
          valueType: 'BOOLEAN',
        },
      };
      checkboxFieldMapper(singleCheckbox, output, from);
    }
  } else {
    const checkboxFieldGroup: TransportLayerCheckboxFieldGroup = {
      config: {
        minimumRequired: current.config.required ? 1 : 0,
        maximumAllowed: current.config.mode === 'RADIO' ? 1 : current.children.length,
      },
      dataLabel: current.dataLabel,
      participant: toParticipantRef(current.participantReference, from),
      type: 'CHECKBOX_GROUP',
    };
    output.fieldGroups.push(checkboxFieldGroup);

    current.children.forEach((child) => {
      if (child.type === 'CHECKBOX') {
        checkboxFieldMapper({ ...child, dataLabel: `${child.index}` }, output, from);
      }
    });
  }
}

function autoFillFieldGroupMapper(
  current: SigningStateFieldNode,
  output: FieldMapperOutput,
  from: MappingFrom
) {
  if (current.type !== 'AUTOFILL_GROUP') {
    return;
  }

  const group: TransportLayerAutofillFieldGroup = {
    config: {},
    dataLabel: current.dataLabel,
    participant: toParticipantRef(current.participantReference, from),
    type: 'AUTOFILL_GROUP',
  };
  output.fieldGroups.push(group);

  current.children.forEach((child) => {
    fieldMapper[child.type](child, output, from);
  });
}
